/**
 * Floating button — scrolls the window to the top.
 * Rendered at the app shell (main.tsx) so it appears on every storefront route.
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFeatures } from '@/hooks/useFeatures'
import { usePublicWhatsAppButton } from '@/hooks/useWhatsAppButton'

const SCROLL_THRESHOLD = 80

function getScrollTop(): number {
  return (
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  )
}

export function ScrollToTopButton() {
  const { pathname } = useLocation()
  const { features } = useFeatures()
  const { data: whatsappConfig } = usePublicWhatsAppButton()
  const [visible, setVisible] = useState(false)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  const hidden = pathname.startsWith('/pos')
  const whatsappOnRight =
    features.enable_whatsapp_button &&
    whatsappConfig?.phone_number &&
    whatsappConfig.position !== 'bottom-left'

  const updateVisibility = useCallback(() => {
    setVisible(getScrollTop() > SCROLL_THRESHOLD)
  }, [])

  useEffect(() => {
    setPortalTarget(document.body)
  }, [])

  useEffect(() => {
    if (hidden) {
      setVisible(false)
      return
    }

    updateVisibility()

    window.addEventListener('scroll', updateVisibility, { passive: true })
    document.addEventListener('scroll', updateVisibility, { passive: true, capture: true })

    return () => {
      window.removeEventListener('scroll', updateVisibility)
      document.removeEventListener('scroll', updateVisibility, { capture: true })
    }
  }, [hidden, pathname, updateVisibility])

  if (!portalTarget || hidden) {
    return null
  }

  const button = (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      title="Back to top"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={cn(
        'fixed z-[90] flex h-12 w-12 items-center justify-center rounded-full',
        'end-4 sm:end-6',
        'bg-primary-600 text-white shadow-lg ring-1 ring-black/10',
        'transition-all duration-300 hover:scale-105 hover:bg-primary-700 hover:shadow-xl',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        visible
          ? 'translate-y-0 scale-100 opacity-100 pointer-events-auto'
          : 'translate-y-2 scale-90 opacity-0 pointer-events-none',
      )}
      style={{
        bottom: whatsappOnRight
          ? 'calc(max(1rem, env(safe-area-inset-bottom, 1rem)) + 4.5rem)'
          : 'max(1rem, env(safe-area-inset-bottom, 1rem))',
      }}
    >
      <ChevronUp className="h-6 w-6" strokeWidth={2.5} aria-hidden />
    </button>
  )

  return createPortal(button, portalTarget)
}
